import { useMemo, useCallback } from 'react';
import { computePayoutFeedback } from '../../../utils/payoutUtils';

/**
 * usePayoutFiltering
 *
 * Encapsulates the heavy filtering/scoping logic for driver payout selection.
 * Returns { computeFilteredPayouts, filteredPayouts, payoutFeedback }.
 */
export default function usePayoutFiltering({
    allDriverPayouts,
    effectiveServiceCityId,
    resolvedExistingPayoutIds,
    resolvedExistingMappings,
    entries,
    isEditMode,
    editData,
    showCorpClientDropdown,
    mappingType,
    selectedClient,
    selectedPackage,
    showRideTypeDropdown,
    selectedRideType,
    editingIndex,
    watchCarTypeId,
    watchDriverPayoutId,
    effectiveSiblings,
}) {
    const computeFilteredPayouts = useCallback(
        (carTypeId, currentPayoutId, currentEntryIndex, allSiblings) => {
            if (!carTypeId) return [];

            let pool = allDriverPayouts.filter(
                (p) => p.carTypeId === carTypeId && p.serviceCityId === effectiveServiceCityId
            );

            // Exclude already-mapped payout IDs (create mode exclusion list)
            // When ride type scoping is active, only exclude payouts that are
            // mapped for the SAME ride type — a payout mapped for A2C should
            // still be available for C2A.
            const currentRideType = showRideTypeDropdown ? selectedRideType?.value : null;
            if (resolvedExistingPayoutIds.length > 0) {
                if (currentRideType && resolvedExistingMappings.length > 0) {
                    // Scope: only exclude payouts from mappings matching this ride type
                    const sameRideTypePayoutIds = new Set(
                        resolvedExistingMappings
                            .filter((m) => m.rideType === currentRideType)
                            .map((m) => m.driverPayout?.id)
                            .filter(Boolean)
                    );
                    pool = pool.filter((p) => !sameRideTypePayoutIds.has(p.id));
                } else {
                    // No ride type selected or no mapping details — exclude all (safe default)
                    const excludeSet = new Set(resolvedExistingPayoutIds);
                    pool = pool.filter((p) => !excludeSet.has(p.id));
                }
            }

            // Time ranges from sibling entries in the list.
            // When the toggle is active, only entries for the SAME
            // client / package / rideType count as siblings — different
            // ones can reuse the same car type / time range without conflict.
            const currentClientId = showCorpClientDropdown && mappingType === 'cc' ? selectedClient?.value : null;
            const currentPackageId = showCorpClientDropdown && mappingType === 'p' ? selectedPackage?.value : null;
            const siblingEntries = entries.filter((e, i) => {
                if (i === currentEntryIndex) return false;
                if (e.carTypeId !== carTypeId || !e.driverPayoutId) return false;
                if (showCorpClientDropdown) {
                    if (mappingType === 'cc' && e.corporateClientId !== currentClientId) return false;
                    if (mappingType === 'p' && e.packagesId !== currentPackageId) return false;
                }
                if (showRideTypeDropdown && e.rideType !== currentRideType) return false;
                return true;
            });
            const occupiedFromEntries = siblingEntries
                .map((e) => allDriverPayouts.find((p) => p.id === e.driverPayoutId))
                .filter(Boolean)
                .map((p) => ({ timeFrom: p.timeFrom, timeTo: p.timeTo }));

            // Time ranges from existing server mappings
            // Also scope by rideType when applicable
            const editRideType = isEditMode ? editData?.rideType : currentRideType;
            const occupiedFromMappings = allSiblings
                .filter((m) => {
                    if (isEditMode && m.id === editData?.id) return false; // skip self
                    if (m.driverPayout?.carTypeId !== carTypeId) return false;
                    // Scope by rideType — different ride types shouldn't block each other
                    if (editRideType && m.rideType && m.rideType !== editRideType) return false;
                    return true;
                })
                .map((m) => ({ timeFrom: m.driverPayout.timeFrom, timeTo: m.driverPayout.timeTo }));

            const occupiedRanges = [...occupiedFromEntries, ...occupiedFromMappings];

            const siblingPayoutIds = new Set(siblingEntries.map((e) => e.driverPayoutId));

            return pool.filter((payout) => {
                if (payout.id === currentPayoutId) return true;
                if (siblingPayoutIds.has(payout.id)) return false;
                const hasOverlap = occupiedRanges.some(
                    (range) => payout.timeFrom < range.timeTo && payout.timeTo > range.timeFrom
                );
                return !hasOverlap;
            });
        },
        [allDriverPayouts, effectiveServiceCityId, resolvedExistingPayoutIds, resolvedExistingMappings, entries, isEditMode, editData, showCorpClientDropdown, mappingType, selectedClient, selectedPackage, showRideTypeDropdown, selectedRideType]
    );

    // Filtered payouts for the current form row
    const filteredPayouts = useMemo(() => {
        const currentPayoutId = isEditMode ? editData?.driverPayoutId : watchDriverPayoutId;
        return computeFilteredPayouts(
            watchCarTypeId,
            currentPayoutId,
            editingIndex,
            isEditMode ? effectiveSiblings : resolvedExistingMappings
        );
    }, [watchCarTypeId, watchDriverPayoutId, editData, editingIndex, computeFilteredPayouts, isEditMode, effectiveSiblings, resolvedExistingMappings]);

    // Payout feedback (when no options available)
    const payoutFeedback = useMemo(() => {
        if (!watchCarTypeId || watchDriverPayoutId) return null;
        if (filteredPayouts.length > 0) return null;

        const allSiblings = isEditMode ? effectiveSiblings : resolvedExistingMappings;
        // Same scoping rule as computeFilteredPayouts
        const currentClientId = showCorpClientDropdown && mappingType === 'cc' ? selectedClient?.value : null;
        const currentPackageId = showCorpClientDropdown && mappingType === 'p' ? selectedPackage?.value : null;
        const currentRideType = showRideTypeDropdown ? selectedRideType?.value : null;
        const siblingEntries = entries.filter((e, i) => {
            if (i === editingIndex) return false;
            if (e.carTypeId !== watchCarTypeId || !e.driverPayoutId) return false;
            if (showCorpClientDropdown) {
                if (mappingType === 'cc' && e.corporateClientId !== currentClientId) return false;
                if (mappingType === 'p' && e.packagesId !== currentPackageId) return false;
            }
            if (showRideTypeDropdown && e.rideType !== currentRideType) return false;
            return true;
        });
        const occupiedRanges = [
            ...siblingEntries
                .map((e) => allDriverPayouts.find((p) => p.id === e.driverPayoutId))
                .filter(Boolean)
                .map((p) => ({ timeFrom: p.timeFrom, timeTo: p.timeTo })),
            ...allSiblings
                .filter((m) => {
                    if (isEditMode && m.id === editData?.id) return false;
                    if (m.driverPayout?.carTypeId !== watchCarTypeId) return false;
                    // Scope by rideType — different ride types shouldn't block each other
                    const feedbackRideType = isEditMode ? editData?.rideType : currentRideType;
                    if (feedbackRideType && m.rideType && m.rideType !== feedbackRideType) return false;
                    return true;
                })
                .map((m) => ({ timeFrom: m.driverPayout.timeFrom, timeTo: m.driverPayout.timeTo })),
        ];
        return computePayoutFeedback({
            allPayouts: allDriverPayouts,
            carTypeId: watchCarTypeId,
            serviceCityId: effectiveServiceCityId,
            occupiedRanges,
        });
    }, [watchCarTypeId, watchDriverPayoutId, filteredPayouts, allDriverPayouts, effectiveServiceCityId, entries, editingIndex, isEditMode, effectiveSiblings, resolvedExistingMappings, editData, showCorpClientDropdown, mappingType, selectedClient, selectedPackage, showRideTypeDropdown, selectedRideType]);

    return { computeFilteredPayouts, filteredPayouts, payoutFeedback };
}
